// Copyright ©️ 2026 eVolve MEP, LLC

import type { ReactNode } from 'react';

import type { EvolveApiRoute, AdditionalRequestConfig } from '@evolvemep/api-ts';
import type { IServerSideGetRowsRequest } from 'ag-grid-enterprise';

import type { KeysOfType } from 'helpers/helper-types';

/** Used for server side grouping. */
export type GroupingSettings<
  TRoute extends EvolveApiRoute<'GET_PAGINATED'>,
  GData extends { entireCount?: number },
  GKey extends KeysOfType<GData, string> = KeysOfType<GData, string>,
> = {
  /** Function to fetch groups */
  fetchGroups: (request: IServerSideGetRowsRequest) => Promise<GData[]>;
  /** Unique ID key of each group. */
  rowId: GKey;
  /** Group label getter function. */
  getGroupLabel: (groupData: GData) => ReactNode;
  /** Left padding to align group titles */
  padding?: number;
  /**
   * This function is used to map additional URL params (or anything else)
   * onto the page fetcher for our main table data (TData).
   * Typically, this is whatever params are needed to fetch the rows for this specific group.
   *
   * If not provided, will instead pass the following param:
   *
   *     {
   *       [rowId]: groupData[rowId],
   *     }
   * */
  configMapper?: (
    groupData: GData,
    request: IServerSideGetRowsRequest,
  ) => AdditionalRequestConfig<'GET_PAGINATED', TRoute>;
  /** Whether or not to auto-expand the group on load. @default true */
  autoExpand?: boolean | ((groupData: GData) => boolean);
};

/**
 * Type-safe wrapper for creating a server-side AG Grid grouping config.
 * Make sure to wrap this call in a memo!
 */
export const createGridGrouping = <
  TRoute extends EvolveApiRoute<'GET_PAGINATED'>,
  GData extends { entireCount?: number },
  GKey extends KeysOfType<GData, string>,
>(
  config: GroupingSettings<TRoute, GData, GKey>,
) => config;
